import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Grid,
  IconButton,
  Typography,
  Tooltip,
  Button,
} from "@mui/material";
import FolderCopyOutlinedIcon from "@mui/icons-material/FolderCopyOutlined";
import CloseIcon from "@mui/icons-material/Close";

const CustomImageUpload = ({
  label,
  supportedFormats,
  onImageChange,
  images,
  deleteImage,
  isViewOnly,
  maxSize,
  isImageValid = true,
}) => {
  const fileInputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageError, setImageError] = useState("");
  const { t } = useTranslation();

  useEffect(() => {
    if (images?.length > 0) {
      setSelectedImage(images[0]);
    }
  }, [images]);

  const handleFileChange = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    let imageErrors = "";
    const maxFileSize = maxSize * 1024 * 1024;
    if (file.size > maxFileSize) {
      imageErrors = t("Common.MaxFileSize");
      event.target.value = "";
    }

    const extension = file.name.split(".").pop().toLowerCase();
    const allowedFormats = supportedFormats.map((f) => f.toLowerCase());

    if (!allowedFormats.includes(extension)) {
      event.target.value = "";
      imageErrors = t("Common.InvalidFileType");
    }

    const newImage = {
      id: 0,
      images: [URL.createObjectURL(file)],
      originalFileName: file.name,
      file,
    };

    setSelectedImage(newImage);
    onImageChange([file]);
    if (imageErrors === "") {
      isImageValid(true);
    } else {
      isImageValid(false);
    }
    event.target.value = ""; // reset
    setImageError(imageErrors);
  };

  const handleRemoveImage = () => {
    if (selectedImage?.id > 0) {
      deleteImage(selectedImage.id);
    }
    setSelectedImage(null);
    onImageChange([]);
    setImageError("");
    isImageValid(true);
  };

  const handleDragAreaClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileChange({ target: { files: [file] } });
    }
  };

  return (
    <div className="cm-customimage-upload">
      <Grid container spacing={2}>
        <Grid item xs={12} textAlign={"center"}>
          {selectedImage && (
            <Grid item xs={12}>
              <Box>
                <Typography variant="h6" mb={1}>
                  {t("Common.Preview")}
                </Typography>
                <Box className="cm-uploadedimg-wrapper">
                  <Box position="relative" className="cm-img-item">
                    <Tooltip title={selectedImage.originalFileName || ""}>
                      <img
                        src={
                          selectedImage?.id > 0
                            ? selectedImage.images
                            : selectedImage.images[0]
                        }
                        alt="uploaded"
                        style={{
                          width: "100px",
                          height: "auto",
                          borderRadius: "50%",
                          display: "flex",
                        }}
                      />
                    </Tooltip>
                    {!isViewOnly && (
                      <IconButton
                        size="small"
                        onClick={handleRemoveImage}
                        style={{
                          position: "absolute",
                          top: "5px",
                          right: "5px",
                          backgroundColor: "var(--headerbg)",
                          borderRadius: "10px",
                        }}
                      >
                        <CloseIcon fontSize="small" color="secondary.text" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}
          <div
            className="drag-area"
            onClick={isViewOnly ? null : handleDragAreaClick}
            onDragOver={isViewOnly ? null : onDragOver}
            onDrop={isViewOnly ? null : onDrop}
            style={{
              borderRadius: "8px",
              textAlign: "center",
              cursor: isViewOnly ? "default" : "pointer",
            }}
          >
            <Button
              variant="contained"
              tabIndex={-1}
              startIcon={<FolderCopyOutlinedIcon />}
              color="secondary"
              sx={{ px: 3, flexWrap: "wrap" }}
            >
              {t("Common.Select files")}
            </Button>
            <Typography
              variant="body1"
              color="secondary.light"
              fontWeight={500}
              mt={1}
              textAlign={"left"}
            >
              {t("Common.Upload files")}{" "}
              <Typography
                variant="body1"
                component={"span"}
                color="secondary.text"
                fontWeight={500}
                sx={{ textDecoration: "underline" }}
                mb={1}
              >
                {t("Common.Choose files")}
              </Typography>
            </Typography>
            {!isViewOnly && (
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: "none" }}
                accept={supportedFormats.map((f) => "." + f).join(",")}
                onChange={handleFileChange}
              />
            )}
            <Typography color="error" variant="body2">
              {imageError}
            </Typography>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default CustomImageUpload;
